<template>
  <div>
    <!-- ── Cabecera ─────────────────────────────────────── -->
    <div class="tl-header">
      <h6 class="section-title mb-0">Pipeline de Creativos — Timeline GoGuau</h6>
      <div class="d-flex align-items-center" style="gap:8px;flex-wrap:wrap">
        <b-button-group size="sm">
          <b-button :variant="view === 'kanban' ? 'primary' : 'outline-secondary'" @click="view = 'kanban'">⬛ Pipeline</b-button>
          <b-button :variant="view === 'calendar' ? 'primary' : 'outline-secondary'" @click="view = 'calendar'">📅 Calendario</b-button>
        </b-button-group>
        <b-button size="sm" variant="success" @click="openForm(null)">+ Nueva pieza</b-button>
      </div>
    </div>

    <!-- ── Alertas rotación Andromeda ────────────────────── -->
    <div v-for="w in rotationWarnings" :key="w.id"
      class="mb-2 p-2 rounded d-flex align-items-start"
      style="background:rgba(245,158,11,.06);border:1px solid rgba(245,158,11,.3);font-size:.78rem">
      <span class="mr-2" style="font-size:1.1rem">🔄</span>
      <div>
        <strong>{{ w.titular }}</strong> lleva <strong>{{ w.days }} días publicada</strong>.
        Andromeda recomienda rotar creativos cada 21–28 días — considera crear una variante.
      </div>
    </div>

    <!-- ══ KANBAN ══════════════════════════════════════════ -->
    <div v-if="view === 'kanban'" class="kanban-board">
      <div v-for="col in STATUSES" :key="col.id" class="kanban-col">
        <div class="kanban-col-header">
          <span>{{ col.icon }} {{ col.label }}</span>
          <b-badge variant="secondary" style="margin-left:6px;font-size:.65rem">{{ piecesInStatus(col.id).length }}</b-badge>
        </div>
        <div class="kanban-cards">
          <div v-for="piece in piecesInStatus(col.id)" :key="piece.id"
            class="kanban-card" @click="openForm(piece)">
            <div class="kanban-card-angle">
              {{ angleIcon(piece.angle) }} {{ piece.angle_name || piece.angle }}
            </div>
            <div class="kanban-card-title">
              {{ piece.copy && piece.copy.titular ? piece.copy.titular : '(sin titular)' }}
            </div>
            <div class="kanban-card-meta">{{ piece.city || '—' }} · {{ piece.service || '—' }}</div>
            <div v-if="piece.image_url" class="kanban-card-thumb">
              <img :src="piece.image_url" alt="" />
            </div>
            <div class="kanban-card-footer">
              <span class="kanban-date">{{ piece.scheduled_date ? fmtDate(piece.scheduled_date) : '—' }}</span>
              <div>
                <b-button size="sm" variant="link" class="p-0 mr-1" title="Retroceder estado"
                  :disabled="col.id === STATUSES[0].id"
                  @click.stop="moveStatus(piece, -1)">←</b-button>
                <b-button size="sm" variant="link" class="p-0" title="Avanzar estado"
                  :disabled="col.id === STATUSES[STATUSES.length - 1].id"
                  @click.stop="moveStatus(piece, +1)">→</b-button>
              </div>
            </div>
          </div>
          <div v-if="piecesInStatus(col.id).length === 0" class="kanban-empty">Sin piezas</div>
        </div>
      </div>
    </div>

    <!-- ══ CALENDARIO ══════════════════════════════════════ -->
    <div v-if="view === 'calendar'">
      <div class="cal-nav">
        <b-button size="sm" variant="outline-secondary" @click="calOffset--">‹</b-button>
        <strong style="font-size:.9rem">{{ calTitle }}</strong>
        <b-button size="sm" variant="outline-secondary" @click="calOffset++">›</b-button>
      </div>
      <div class="cal-grid">
        <div v-for="day in ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb']" :key="day" class="cal-weekday">{{ day }}</div>
        <div v-for="cell in calCells" :key="cell.iso || cell.blank + Math.random()"
          :class="['cal-day', cell.blank ? 'cal-blank' : '', cell.isToday ? 'cal-today' : '']">
          <div v-if="!cell.blank">
            <div class="cal-day-num">{{ cell.num }}</div>
            <div v-for="p in piecesOnDay(cell.iso)" :key="p.id"
              class="cal-piece" :class="`cal-piece-${p.status}`"
              @click="openForm(p)">
              {{ statusIcon(p.status) }} {{ p.copy && p.copy.titular ? p.copy.titular.slice(0, 20) : '—' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ MODAL EDICIÓN ════════════════════════════════════ -->
    <b-modal v-model="form.show" size="lg"
      :title="form.id ? '✏️ Editar pieza' : '✦ Nueva pieza de contenido'"
      ok-title="Guardar" cancel-title="Cancelar"
      @ok.prevent="savePiece"
      @hidden="resetForm">

      <b-row>
        <!-- Estado + fechas -->
        <b-col md="6">
          <b-form-group label="Estado:" label-class="font-weight-bold" class="mb-2">
            <b-form-select v-model="form.status" size="sm"
              :options="STATUSES.map(s => ({ value: s.id, text: s.icon + ' ' + s.label }))" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Fecha de publicación:" label-class="font-weight-bold" class="mb-2">
            <b-form-input v-model="form.scheduled_date" type="date" size="sm" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Ciudad:" label-class="font-weight-bold" class="mb-2">
            <b-form-select v-model="form.city" size="sm"
              :options="['Armenia','Ibagué','Pereira','Popayán','Cali','Bogotá','Otra']" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Servicio:" label-class="font-weight-bold" class="mb-2">
            <b-form-select v-model="form.service" size="sm"
              :options="['Paseo de perros','Guardería','Adiestramiento','Veterinaria','General']" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Budget diario (COP):" label-class="font-weight-bold" class="mb-2">
            <b-form-input v-model.number="form.budget_daily" type="number" size="sm" min="0" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Ángulo Andromeda:" label-class="font-weight-bold" class="mb-2">
            <b-form-select v-model="form.angle" size="sm"
              :options="ANDROMEDA_ANGLES.map(a => ({ value: a.id, text: a.icon + ' ' + a.name }))"
              @change="onAngleChange" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Principio psicológico:" label-class="font-weight-bold" class="mb-2">
            <b-form-select v-model="form.principle" size="sm"
              :options="PRINCIPLES_SHORT.map(p => ({ value: p, text: p }))" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Responsable:" label-class="font-weight-bold" class="mb-2">
            <b-form-input v-model="form.responsible" size="sm" placeholder="Nombre del gestor" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Notas:" label-class="font-weight-bold" class="mb-2">
            <b-form-input v-model="form.notes" size="sm" placeholder="Contexto adicional…" />
          </b-form-group>
        </b-col>
      </b-row>

      <hr class="my-3">
      <div style="font-size:.78rem;font-weight:700;color:#6b7094;text-transform:uppercase;letter-spacing:.07em;margin-bottom:8px">Copy</div>
      <b-row>
        <b-col md="6">
          <b-form-group label="Titular:" label-class="font-weight-bold" class="mb-2">
            <b-form-input v-model="form.copy.titular" size="sm" maxlength="60"
              :placeholder="'máx 40 chars — ' + (form.copy.titular ? form.copy.titular.length + '/40' : '0/40')" />
          </b-form-group>
          <b-form-group label="Texto principal:" label-class="font-weight-bold" class="mb-2">
            <b-form-textarea v-model="form.copy.texto_principal" rows="3" size="sm" maxlength="200" />
          </b-form-group>
          <b-form-group label="CTA:" label-class="font-weight-bold" class="mb-2">
            <b-form-input v-model="form.copy.cta" size="sm" maxlength="25" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Segmentación Andromeda:" label-class="font-weight-bold" class="mb-2">
            <b-form-textarea v-model="form.copy.segmentacion" rows="2" size="sm" />
          </b-form-group>
          <b-form-group label="¿Por qué funciona?" label-class="font-weight-bold" class="mb-2">
            <b-form-textarea v-model="form.copy.por_que" rows="2" size="sm" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Prompt de imagen:" label-class="font-weight-bold" class="mb-2">
        <b-form-textarea v-model="form.image_prompt" rows="3" size="sm" placeholder="Prompt usado para generar la imagen en Gemini…" />
      </b-form-group>

      <div v-if="form.image_url" class="mt-2 text-center">
        <img :src="form.image_url" alt="Creativo" style="max-width:100%;max-height:240px;border-radius:8px;border:1px solid #dee2e6" />
        <div style="font-size:.7rem;color:#6b7094;margin-top:4px">
          URL: <a :href="form.image_url" target="_blank">ver imagen</a>
        </div>
      </div>
      <b-form-group label="URL de imagen:" label-class="font-weight-bold" class="mt-2">
        <b-form-input v-model="form.image_url" size="sm" placeholder="https://…" />
      </b-form-group>

      <!-- Referencia al anuncio original -->
      <div v-if="form.ad_name_ref || form.campaign_name_ref" class="mt-2 p-2 rounded"
        style="background:#f0f4ff;font-size:.72rem;color:#6b7094">
        Generado desde: <strong>{{ form.ad_name_ref }}</strong>
        <span v-if="form.campaign_name_ref"> · campaña: {{ form.campaign_name_ref }}</span>
      </div>

      <div v-if="form.saving" class="text-center py-2">
        <b-spinner small variant="primary" /> Guardando…
      </div>
      <CAlert v-if="form.error" color="danger" class="mt-2">{{ form.error }}</CAlert>

      <template #modal-footer>
        <div class="d-flex justify-content-between w-100">
          <b-button v-if="form.id" variant="outline-danger" size="sm" @click="deletePiece">🗑 Eliminar</b-button>
          <div class="ml-auto" style="gap:8px;display:flex">
            <b-button variant="outline-secondary" size="sm" @click="form.show = false">Cancelar</b-button>
            <b-button variant="primary" size="sm" :disabled="form.saving" @click="savePiece">Guardar</b-button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
const STATUSES = [
  { id: 'idea',       label: 'Idea',       icon: '💡' },
  { id: 'borrador',   label: 'Borrador',   icon: '📝' },
  { id: 'revision',   label: 'Revisión',   icon: '👁' },
  { id: 'aprobado',   label: 'Aprobado',   icon: '✅' },
  { id: 'programado', label: 'Programado', icon: '📅' },
  { id: 'publicado',  label: 'Publicado',  icon: '🚀' },
]

const ANDROMEDA_ANGLES = [
  { id: 'dolor',        icon: '🔴', name: 'Directo al Dolor' },
  { id: 'social',       icon: '⭐', name: 'Prueba Social' },
  { id: 'educativo',    icon: '🧠', name: 'Educativo' },
  { id: 'aspiracional', icon: '✨', name: 'Aspiracional' },
]

const PRINCIPLES_SHORT = [
  'Make it about them — Hazlo sobre ellos',
  'Make it an event — Conviértelo en un evento',
  'Powerful demonstration — Demostración con impacto',
  'Show unquestionable proof — Prueba irrefutable',
  'Change a daily behavior — Cambia un comportamiento diario',
  'Sell the dream — Vende el sueño',
  'Help them rebel — Ayúdalos a rebelarse',
]

const ROTATION_DAYS = 28

function blankCopy () {
  return { titular: '', texto_principal: '', cta: '', segmentacion: '', por_que: '' }
}

function blankForm () {
  return {
    show: false,
    saving: false,
    error: null,
    id: null,
    status: 'idea',
    city: 'Armenia',
    service: 'Paseo de perros',
    angle: 'dolor',
    angle_name: 'Directo al Dolor',
    principle: PRINCIPLES_SHORT[0],
    scheduled_date: '',
    budget_daily: 0,
    responsible: '',
    notes: '',
    copy: blankCopy(),
    image_url: '',
    image_prompt: '',
    ad_name_ref: '',
    campaign_name_ref: '',
  }
}

export default {
  name: 'MetaTimeline',

  data () {
    return {
      STATUSES,
      ANDROMEDA_ANGLES,
      PRINCIPLES_SHORT,
      pieces: [],
      view: 'kanban',
      calOffset: 0,
      form: blankForm(),
      unsubscribe: null,
    }
  },

  computed: {
    rotationWarnings () {
      const now = Date.now()
      return this.pieces
        .filter(p => {
          if (p.status !== 'publicado' || !p.scheduled_date) return false
          const diff = Math.floor((now - new Date(p.scheduled_date).getTime()) / 86400000)
          return diff >= ROTATION_DAYS
        })
        .map(p => ({
          id: p.id,
          titular: p.copy && p.copy.titular ? p.copy.titular : '(sin titular)',
          days: Math.floor((now - new Date(p.scheduled_date).getTime()) / 86400000),
        }))
    },

    calTitle () {
      const d = this.calMonth
      return d.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })
    },

    calMonth () {
      const d = new Date()
      d.setDate(1)
      d.setMonth(d.getMonth() + this.calOffset)
      return d
    },

    calCells () {
      const first = new Date(this.calMonth)
      const year  = first.getFullYear()
      const month = first.getMonth()
      const daysInMonth = new Date(year, month + 1, 0).getDate()
      const startDow = first.getDay()
      const todayStr = new Date().toISOString().slice(0, 10)
      const cells = []
      for (let i = 0; i < startDow; i++) cells.push({ blank: true })
      for (let d = 1; d <= daysInMonth; d++) {
        const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
        cells.push({ blank: false, num: d, iso, isToday: iso === todayStr })
      }
      return cells
    },
  },

  created () {
    this.subscribeFirestore()
  },

  beforeDestroy () {
    if (this.unsubscribe) this.unsubscribe()
  },

  methods: {
    subscribeFirestore () {
      const db = this.$firebase.firestore()
      this.unsubscribe = db.collection('meta_timeline')
        .orderBy('created_at', 'desc')
        .onSnapshot(snap => {
          this.pieces = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        }, err => {
          console.error('[MetaTimeline] Firestore error:', err)
        })
    },

    piecesInStatus (statusId) {
      return this.pieces.filter(p => p.status === statusId)
    },

    piecesOnDay (iso) {
      return this.pieces.filter(p => p.scheduled_date === iso)
    },

    angleIcon (angleId) {
      const a = ANDROMEDA_ANGLES.find(x => x.id === angleId)
      return a ? a.icon : '📌'
    },

    statusIcon (statusId) {
      const s = STATUSES.find(x => x.id === statusId)
      return s ? s.icon : '📌'
    },

    fmtDate (iso) {
      if (!iso) return '—'
      const d = new Date(iso + 'T00:00:00')
      return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })
    },

    onAngleChange (id) {
      const a = ANDROMEDA_ANGLES.find(x => x.id === id)
      if (a) this.form.angle_name = a.name
    },

    openForm (piece) {
      if (piece) {
        this.form = {
          show: true,
          saving: false,
          error: null,
          id: piece.id,
          status: piece.status || 'idea',
          city: piece.city || 'Armenia',
          service: piece.service || 'Paseo de perros',
          angle: piece.angle || 'dolor',
          angle_name: piece.angle_name || '',
          principle: piece.principle || PRINCIPLES_SHORT[0],
          scheduled_date: piece.scheduled_date || '',
          budget_daily: piece.budget_daily || 0,
          responsible: piece.responsible || '',
          notes: piece.notes || '',
          copy: piece.copy ? { ...piece.copy } : blankCopy(),
          image_url: piece.image_url || '',
          image_prompt: piece.image_prompt || '',
          ad_name_ref: piece.ad_name_ref || '',
          campaign_name_ref: piece.campaign_name_ref || '',
        }
      } else {
        this.form = blankForm()
        this.form.show = true
      }
    },

    // Llamado desde MetaAdsDashboard.vue vía $refs.timeline.openWithData(...)
    openWithData (data) {
      this.form = {
        ...blankForm(),
        show: true,
        ...data,
        copy: data.copy ? { ...data.copy } : blankCopy(),
      }
    },

    resetForm () {
      this.form = blankForm()
    },

    async savePiece () {
      this.form.saving = true
      this.form.error = null
      try {
        const db   = this.$firebase.firestore()
        const now  = new Date()
        const data = {
          status:           this.form.status,
          city:             this.form.city,
          service:          this.form.service,
          angle:            this.form.angle,
          angle_name:       this.form.angle_name,
          principle:        this.form.principle,
          scheduled_date:   this.form.scheduled_date || null,
          budget_daily:     this.form.budget_daily || 0,
          responsible:      this.form.responsible,
          notes:            this.form.notes,
          copy:             { ...this.form.copy },
          image_url:        this.form.image_url,
          image_prompt:     this.form.image_prompt,
          ad_name_ref:      this.form.ad_name_ref,
          campaign_name_ref: this.form.campaign_name_ref,
          updated_at:       now,
        }
        if (this.form.id) {
          await db.collection('meta_timeline').doc(this.form.id).update(data)
        } else {
          data.created_at = now
          await db.collection('meta_timeline').add(data)
        }
        this.form.show = false
      } catch (err) {
        this.form.error = `Error guardando: ${err.message}`
      } finally {
        this.form.saving = false
      }
    },

    async deletePiece () {
      if (!confirm('¿Eliminar esta pieza del timeline?')) return
      try {
        const db = this.$firebase.firestore()
        await db.collection('meta_timeline').doc(this.form.id).delete()
        this.form.show = false
      } catch (err) {
        this.form.error = `Error eliminando: ${err.message}`
      }
    },

    async moveStatus (piece, delta) {
      const idx = STATUSES.findIndex(s => s.id === piece.status)
      const next = STATUSES[idx + delta]
      if (!next) return
      try {
        const db = this.$firebase.firestore()
        await db.collection('meta_timeline').doc(piece.id).update({
          status: next.id,
          updated_at: new Date(),
        })
      } catch (err) {
        console.error('[MetaTimeline] moveStatus error:', err)
      }
    },
  },
}
</script>

<style scoped>
.section-title {
  font-size: .78rem;
  font-weight: 700;
  color: #6b7094;
  text-transform: uppercase;
  letter-spacing: .1em;
  margin: 0;
}
.tl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin: 20px 0 12px;
}

/* ── Kanban ─────────────────────────────────────────── */
.kanban-board {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 8px;
}
@media (max-width: 992px) { .kanban-board { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 576px) { .kanban-board { grid-template-columns: repeat(2, 1fr); } }

.kanban-col {
  min-width: 160px;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 8px;
}
.kanban-col-header {
  font-size: .72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #495057;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}
.kanban-cards { display: flex; flex-direction: column; gap: 8px; }

.kanban-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  transition: box-shadow .15s, border-color .15s;
}
.kanban-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,.12);
  border-color: #3b82f6;
}
.kanban-card-angle {
  font-size: .65rem;
  color: #6b7094;
  margin-bottom: 4px;
}
.kanban-card-title {
  font-size: .8rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 4px;
  word-break: break-word;
}
.kanban-card-meta {
  font-size: .68rem;
  color: #6b7094;
  margin-bottom: 6px;
}
.kanban-card-thumb img {
  width: 100%;
  border-radius: 4px;
  max-height: 80px;
  object-fit: cover;
  margin-bottom: 6px;
}
.kanban-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.kanban-date { font-size: .65rem; color: #6b7094; }
.kanban-empty {
  font-size: .72rem;
  color: #adb5bd;
  text-align: center;
  padding: 16px 0;
  border: 1px dashed #dee2e6;
  border-radius: 6px;
}

/* ── Calendario ─────────────────────────────────────── */
.cal-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 10px;
}
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}
.cal-weekday {
  text-align: center;
  font-size: .68rem;
  font-weight: 700;
  color: #6b7094;
  padding: 4px 0;
  text-transform: uppercase;
}
.cal-day {
  min-height: 80px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 4px;
  font-size: .72rem;
}
.cal-blank { border: none; background: transparent; }
.cal-today { background: #eff6ff; border-color: #3b82f6; }
.cal-day-num {
  font-size: .68rem;
  font-weight: 700;
  color: #6b7094;
  margin-bottom: 4px;
}
.cal-piece {
  font-size: .65rem;
  padding: 2px 4px;
  border-radius: 3px;
  margin-bottom: 2px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #e0e7ff;
  color: #3730a3;
}
.cal-piece-publicado  { background: #dcfce7; color: #166534; }
.cal-piece-aprobado   { background: #d1fae5; color: #065f46; }
.cal-piece-programado { background: #dbeafe; color: #1e40af; }
.cal-piece-revision   { background: #fef9c3; color: #854d0e; }
.cal-piece-borrador   { background: #f3f4f6; color: #374151; }
.cal-piece-idea       { background: #fce7f3; color: #9d174d; }

@media (max-width: 576px) {
  .cal-day { min-height: 50px; }
  .cal-piece { display: none; }
}
</style>
